import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeSlash, EnvelopeSimple, Lock, User, Buildings } from '@phosphor-icons/react';
import MfaEnrollmentWizard from '../components/auth/MfaEnrollmentWizard';
import SignupUnavailable from '../components/auth/SignupUnavailable';
import { useSignupPolicy } from '../hooks/useSignupPolicy';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agency_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enrollChallenge, setEnrollChallenge] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { enabled: signupEnabled, loading: policyLoading } = useSignupPolicy();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData.email, formData.password, formData.name, formData.agency_name);
      if (result?.mfaRequired) {
        toast.success('Account created — set up multi-factor authentication to continue.');
        setEnrollChallenge(result.challengeToken);
      } else {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (!policyLoading && !signupEnabled) {
    return <SignupUnavailable />;
  }

  if (enrollChallenge) {
    return (
      <MfaEnrollmentWizard
        challengeToken={enrollChallenge}
        email={formData.email}
        onComplete={() => navigate('/dashboard')}
        onCancel={() => { setEnrollChallenge(null); navigate('/login'); }}
      />
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Visual */}
      <div
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #312E81 0%, #1E1B4B 100%)',
        }}
      >
        <div className="max-w-lg text-center z-10">
          <h2
            className="text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Manrope' }}
          >
            Start Billing Smarter Today
          </h2>
          <p className="text-indigo-200 text-lg">
            Join home health agencies across Ohio using AZAI to simplify their Medicaid billing workflow.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="text-white font-semibold">ODM & DODD Ready</p>
              <p className="text-indigo-200 text-sm">Pre-loaded Ohio codes</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="text-white font-semibold">Auto Unit Calc</p>
              <p className="text-indigo-200 text-sm">Save time on entries</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="text-white font-semibold">Claim Templates</p>
              <p className="text-indigo-200 text-sm">Reusable claim types</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="text-white font-semibold">Claim Recycle</p>
              <p className="text-indigo-200 text-sm">Copy past claims</p>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-700/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl" />
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">AZ</span>
            </div>
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              AZAI Billing
            </span>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                Create your account
              </CardTitle>
              <CardDescription className="text-slate-500">
                Get started with your free account
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">
                      Full Name
                    </Label>
                    <div className="relative">
                      <User
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <Input
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        className="pl-10"
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="agency_name" className="text-slate-700">
                      Agency Name
                    </Label>
                    <div className="relative">
                      <Buildings
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <Input
                        id="agency_name"
                        name="agency_name"
                        placeholder="ABC Home Care"
                        value={formData.agency_name}
                        onChange={handleChange}
                        className="pl-10"
                        data-testid="register-agency-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">
                    Email
                  </Label>
                  <div className="relative">
                    <EnvelopeSimple
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="name@agency.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700">
                      Password
                    </Label>
                    <div className="relative">
                      <Lock
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        className="pl-10"
                        required
                        data-testid="register-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700">
                      Confirm
                    </Label>
                    <div className="relative">
                      <Lock
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="pl-10"
                        required
                        data-testid="register-confirm-password-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="showPass"
                    checked={showPassword}
                    onChange={() => setShowPassword(!showPassword)}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="showPass" className="text-sm text-slate-600 cursor-pointer">
                    Show passwords
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-indigo-900 hover:bg-indigo-800 h-11 mt-2"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-500">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-indigo-900 font-medium hover:underline"
                  data-testid="login-link"
                >
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
